// src/lib/http.js
import axios from 'axios'
import router from '@/router'
import { useSessionStore } from '@/stores/session'
import { useGatewaySessionPromptStore } from '@/stores/gatewaySessionPrompt'

const http = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
})

http.interceptors.request.use((config) => {
  const session = useSessionStore()
  if (session?.token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${session.token}` }
  }
  return config
})

let redirecting = false
async function forceLogout() {
  if (redirecting) return
  redirecting = true
  const session = useSessionStore()
  session.clear()

  const currentRoute = router.currentRoute.value
  // Prevent redirect loop if already on login page
  if (currentRoute.name !== 'login') {
    const current = currentRoute.fullPath
    try {
      await router.replace({ name: 'login', query: { redirect: current } })
    } finally {
      redirecting = false
    }
  } else {
    redirecting = false
  }
}

const UNAUTHENTICATED_PATHS = ['/auth/login', '/auth/verify-mfa']

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status
    const url = err?.config?.url || ''
    if (UNAUTHENTICATED_PATHS.some((p) => url.includes(p))) {
      return Promise.reject(err)
    }

    if (status === 419 && !err.config?._gatewaySessionRetried) {
      const confirmed = await useGatewaySessionPromptStore().open()
      if (confirmed) {
        err.config._gatewaySessionRetried = true
        window.location.reload()
        return new Promise(() => {})
      }
      // user chose to log out instead of confirming their password
      await forceLogout()
      return Promise.resolve({ data: null, status: 419 })
    }

    if (status === 401) {
      await forceLogout()
      return Promise.resolve({ data: null, status: 401 })
    }

    return Promise.reject(err)
  }
)

export default http
